import React, { Fragment, useEffect, useState } from 'react';
import { makeStyles, Theme, Grid, Paper, List } from '@material-ui/core';

import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';

import { SearchType } from '@backstage/plugin-search';
import {
  SearchBar,
  SearchFilter,
  SearchResult,
  SearchPagination,
  useSearch,
} from '@backstage/plugin-search-react';
import {
  CatalogIcon,
  Content,
  Progress,
  ResponseErrorPanel,
  DocsIcon,
  Header,
  Page,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { searchTypes } from './searchTypes';
import Assignment from '@material-ui/icons/Assignment';
import { TaskSearchResultListItem } from '@internal/backstage-plugin-sev2';

import { AdrSearchResultListItem, AdrSearchResultListItemProps } from '@backstage-community/plugin-adr';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityReadPermission } from '@backstage/plugin-catalog-common/alpha';
import { IndexableResult } from '@backstage/plugin-search-common';
import {
  AllowedSystemsEntityFilter,
  useAllowedSystemsEntityFilter,
} from '../../hooks/useAllowedSystemsEntityFilter';

const useStyles = makeStyles((theme: Theme) => ({
  bar: {
    padding: theme.spacing(1, 0),
  },
  filters: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  filter: {
    '& + &': {
      marginTop: theme.spacing(2.5),
    },
  },
}));

const normalizeEntityRef = (ref: string) => ref.toLocaleLowerCase('en-US');

const extractEntityRefFromResult = (result: IndexableResult): string | undefined => {
  const document = result.document as {
    authorization?: { resourceRef?: string };
    entityRef?: string;
  };

  return document.authorization?.resourceRef ?? document.entityRef;
};

type AllowedSystemsFilteredResultsProps = {
  results: IndexableResult[];
  filter?: AllowedSystemsEntityFilter;
  children: (results: IndexableResult[], filtering: boolean) => React.ReactNode;
};

const AllowedSystemsFilteredResults = ({
  results,
  filter,
  children,
}: AllowedSystemsFilteredResultsProps) => {
  const catalogApi = useApi(catalogApiRef);
  const [filteredResults, setFilteredResults] = useState(results);
  const [filtering, setFiltering] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setFilteredResults(results);

      if (!filter) {
        setFiltering(false);
        setError(undefined);
        return;
      }

      const entityRefs: string[] = [];
      const seen = new Set<string>();
      for (const result of results) {
        const ref = extractEntityRefFromResult(result);
        if (!ref) {
          continue;
        }
        const normalized = normalizeEntityRef(ref);
        if (!seen.has(normalized)) {
          seen.add(normalized);
          entityRefs.push(ref);
        }
      }

      if (entityRefs.length === 0) {
        setFiltering(false);
        setError(undefined);
        return;
      }

      setFiltering(true);
      try {
        const { items } = await catalogApi.getEntitiesByRefs({ entityRefs });
        const allowedRefs = new Set<string>();

        items.forEach((entity, index) => {
          if (entity && filter.filterEntity(entity)) {
            allowedRefs.add(normalizeEntityRef(entityRefs[index]));
          }
        });

        const nextResults = results.filter(result => {
          const ref = extractEntityRefFromResult(result);
          if (!ref) {
            return true;
          }
          return allowedRefs.has(normalizeEntityRef(ref));
        });

        if (!cancelled) {
          setFilteredResults(nextResults);
          setError(undefined);
        }
      } catch (caughtError) {
        if (!cancelled) {
          const wrappedError =
            caughtError instanceof Error
              ? caughtError
              : new Error(String(caughtError));
          setError(wrappedError);
          setFilteredResults([]);
        }
      } finally {
        if (!cancelled) {
          setFiltering(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [results, filter, catalogApi]);

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return <>{children(filteredResults, filtering)}</>;
};

const SearchPage = () => {
  const classes = useStyles();
  const { types } = useSearch();
  const catalogApi = useApi(catalogApiRef);
  const {
    filter: allowedSystemsFilter,
    loading: allowedSystemsLoading,
    error: allowedSystemsError,
  } = useAllowedSystemsEntityFilter();

  if (allowedSystemsLoading) {
    return (
      <Page themeId="home">
        <Header title="Search" />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (allowedSystemsError) {
    return (
      <Page themeId="home">
        <Header title="Search" />
        <Content>
          <ResponseErrorPanel error={allowedSystemsError} />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="home">
      <Header title="Search" />
      <Content>
        <Grid container direction="row">
          <Grid item xs={12}>
            <SearchBar debounceTime={300} placeholder='Search by component name or task (e.g. "T123" or "T title")' />
          </Grid>
          <Grid item xs={3}>
            <SearchType.Accordion name="Result Type" types={searchTypes} />
            <Paper className={classes.filters}>
              {types.includes('techdocs') && (
                <SearchFilter.Select
                  className={classes.filter}
                  label="Entity"
                  name="name"
                  values={async () => {
                    // Return a list of entities which are documented.
                    const { items } = await catalogApi.getEntities({
                      fields: ['metadata.name'],
                      filter: {
                        'metadata.annotations.backstage.io/techdocs-ref':
                          CATALOG_FILTER_EXISTS,
                      },
                    });

                    const names = items.map(entity => entity.metadata.name);
                    names.sort();
                    return names;
                  }}
                />
              )}
              <SearchFilter.Select
                className={classes.filter}
                label="Kind"
                name="kind"
                values={['Component', 'Template']}
              />
              <SearchFilter.Checkbox
                className={classes.filter}
                label="Lifecycle"
                name="lifecycle"
                values={['experimental', 'production']}
              />
            </Paper>
          </Grid>
          <Grid item xs={9}>
            <SearchPagination />
            <SearchResult>
              {({ results }) => (
                <AllowedSystemsFilteredResults
                  results={results}
                  filter={allowedSystemsFilter}
                >
                  {(filteredResults, filtering) => (
                    <>
                      {filtering && <Progress />}
                      <List>
                        {filteredResults.map(result => {
                          // result.type is the index type defined by the collator.
                          switch (result.type) {
                            case 'software-catalog':
                              return (
                                <CatalogSearchResultListItem
                                  key={result.document.location}
                                  result={result.document}
                                  highlight={result.highlight}
                                  icon={<CatalogIcon />}
                                />
                              );
                            case 'techdocs':
                              return (
                                <TechDocsSearchResultListItem
                                  key={result.document.location}
                                  result={result.document}
                                  highlight={result.highlight}
                                  icon={<DocsIcon />}
                                />
                              );
                            case 'task':
                              return (
                                <TaskSearchResultListItem
                                  key={result.document.location}
                                  result={result.document}
                                  highlight={result.highlight}
                                  icon={<Assignment />}
                                />
                              );
                            case 'adr':
                              const resultDocument = result.document as AdrSearchResultListItemProps['result'];
                              return (
                                <RequirePermission
                                  key={resultDocument?.location}
                                  resourceRef={resultDocument?.entityRef}
                                  permission={catalogEntityReadPermission}
                                  errorPage={
                                    <Fragment />
                                  }
                                >
                                  <AdrSearchResultListItem
                                    key={resultDocument?.location}
                                    result={resultDocument}
                                    highlight={result.highlight}
                                    icon={<DocsIcon />}
                                  />
                                </RequirePermission>
                              );
                            default:
                              return null;
                          }
                        })}
                      </List>
                    </>
                  )}
                </AllowedSystemsFilteredResults>
              )}
            </SearchResult>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export const searchPage = <SearchPage />;
